import { useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { api } from '@/shared/api/axios';
import toast from 'react-hot-toast';
import { Send } from 'lucide-react';

export interface EmailSendModalProps {
  open: boolean;
  onClose: () => void;
  initialTo: string;
  initialSubject: string;
  initialHtml: string;
  onSent?: () => void;
}

/**
 * Reusable pre-filled email send modal.
 * Shows editable To/Subject fields and a rendered HTML body preview.
 * Sends via POST /api/email-templates/send.
 */
export function EmailSendModal({
  open,
  onClose,
  initialTo,
  initialSubject,
  initialHtml,
  onSent,
}: EmailSendModalProps) {
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState(initialSubject);
  const [sending, setSending] = useState(false);

  // Reset fields when modal opens with new data
  const [lastInitial, setLastInitial] = useState('');
  const key = `${initialTo}:${initialSubject}`;
  if (key !== lastInitial && open) {
    setTo(initialTo);
    setSubject(initialSubject);
    setLastInitial(key);
  }

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error('Recipient email is required.');
      return;
    }
    if (!subject.trim()) {
      toast.error('Subject is required.');
      return;
    }
    setSending(true);
    try {
      await api.post('/api/email-templates/send', {
        to: to.trim(),
        subject: subject.trim(),
        html: initialHtml,
      });
      toast.success('Email sent successfully.');
      onSent?.();
      onClose();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to send email.';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send Email"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending}>
            <Send className="h-4 w-4 mr-1" />
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-2">
        <Input
          name="email-to"
          label="To"
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="recipient@example.com"
        />
        <Input
          name="email-subject"
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <div>
          <label className="mb-1.5 block text-sm font-medium">Email Preview</label>
          <div
            className="max-h-[300px] overflow-auto rounded-md border border-gray-200 bg-white p-4 text-sm"
            dangerouslySetInnerHTML={{ __html: initialHtml }}
          />
        </div>
      </div>
    </Modal>
  );
}
